import React from 'react';
import { Link } from 'react-router-dom';

export default function PrivacyPage() {
  return (
    <div
      data-testid="privacy-page"
      className="min-h-screen bg-background text-foreground"
    >
      <header className="border-b border-border bg-surface/60 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/login"
            className="text-sm font-semibold tracking-tight text-primary hover:opacity-80 transition-opacity"
            data-testid="privacy-back-link"
          >
            ← Lovi Integration
          </Link>
          <span className="text-xs text-muted-foreground uppercase tracking-wider">
            Legal
          </span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="space-y-2 mb-10">
          <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
            Effective Date · February 12, 2026
          </p>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight">
            Privacy Policy
          </h1>
          <p className="text-base text-muted-foreground pt-2">
            Lovi Integration · How we collect, use and safeguard data accessed
            through TikTok Shop and Intuit QuickBooks Online APIs.
          </p>
        </div>

        <section className="prose prose-slate dark:prose-invert max-w-none space-y-6 text-base leading-relaxed">
          <h2 className="text-xl font-semibold mt-8">1. Who We Are</h2>
          <p className="text-muted-foreground">
            Lovi Integration is operated by CPN Trade Inc. (“we”, “our”,
            “us”), doing business as Lovi Creation. We are the data
            controller for personal information processed by this Service.
          </p>

          <h2 className="text-xl font-semibold mt-8">2. Information We Collect</h2>
          <p className="text-muted-foreground">
            We collect and process the following categories of information:
          </p>
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li>
              <strong>Account data:</strong> email, name and hashed password
              used to access the Lovi Integration dashboard.
            </li>
            <li>
              <strong>TikTok Shop data:</strong> orders, line items, customer
              shipping addresses, fees and refunds retrieved through the
              TikTok Shop Open API.
            </li>
            <li>
              <strong>QuickBooks Online data:</strong> chart of accounts,
              customers, items, invoices, sales receipts, payments, journal
              entries and deposits accessed via the Intuit QuickBooks API.
            </li>
            <li>
              <strong>Operational logs:</strong> timestamps, request IDs and
              error traces required for audit and troubleshooting.
            </li>
          </ul>

          <h2 className="text-xl font-semibold mt-8">3. How We Use Data</h2>
          <p className="text-muted-foreground">
            We use this data solely to:
          </p>
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li>Synchronize orders, returns and settlements between TikTok Shop and QuickBooks Online.</li>
            <li>Reconcile clearing accounts and produce accounting reports.</li>
            <li>Detect duplicate customers and de-duplicate records.</li>
            <li>Provide support, audit trails and operational diagnostics.</li>
          </ul>

          <h2 className="text-xl font-semibold mt-8">4. Legal Basis</h2>
          <p className="text-muted-foreground">
            We process personal data on the legal basis of performance of a
            contract with our customer (CPN Trade Inc.), our legitimate
            business interests in reconciling commerce data, and where
            applicable, your consent provided when authorizing the OAuth
            connection.
          </p>

          <h2 className="text-xl font-semibold mt-8">5. Data Sharing</h2>
          <p className="text-muted-foreground">
            We do not sell or rent personal data. We share data only with:
          </p>
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li>
              <strong>TikTok Shop &amp; Intuit:</strong> as required to perform
              the integration you have authorized.
            </li>
            <li>
              <strong>Infrastructure providers:</strong> hosting and database
              services bound by confidentiality and data-processing
              agreements.
            </li>
            <li>
              <strong>Authorities:</strong> when required by law or to
              protect our rights.
            </li>
          </ul>

          <h2 className="text-xl font-semibold mt-8">6. Data Retention</h2>
          <p className="text-muted-foreground">
            We retain order, settlement and accounting data for as long as
            necessary to fulfil tax, audit and contractual obligations,
            typically seven (7) years. OAuth refresh tokens are rotated and
            stored encrypted at rest. You can request deletion of your data
            at any time by emailing us.
          </p>

          <h2 className="text-xl font-semibold mt-8">7. Security</h2>
          <p className="text-muted-foreground">
            We use industry-standard safeguards including TLS in transit,
            password hashing with bcrypt, encrypted secrets, role-based
            access control and audit logging. No system is 100% secure, but
            we work to minimize risk.
          </p>

          <h2 className="text-xl font-semibold mt-8">8. Your Rights</h2>
          <p className="text-muted-foreground">
            Subject to applicable law (GDPR, CCPA), you may request access,
            correction, deletion, or portability of your personal data, and
            object to or restrict processing. To exercise these rights,
            contact us at{' '}
            <a
              href="mailto:admin@lovicreation.shop"
              className="text-primary underline underline-offset-4 hover:opacity-80"
              data-testid="privacy-contact-email"
            >
              admin@lovicreation.shop
            </a>
            .
          </p>

          <h2 className="text-xl font-semibold mt-8">9. Disconnecting the Service</h2>
          <p className="text-muted-foreground">
            You can revoke this app’s access to your TikTok Shop or
            QuickBooks Online account at any time from the respective
            provider’s “Connected apps” settings, or by disconnecting from
            within the Lovi Integration dashboard. Upon revocation, ongoing
            synchronization stops immediately.
          </p>

          <h2 className="text-xl font-semibold mt-8">10. International Transfers</h2>
          <p className="text-muted-foreground">
            Data may be processed in the United States. By using the Service
            you consent to such transfers, subject to appropriate safeguards.
          </p>

          <h2 className="text-xl font-semibold mt-8">11. Changes</h2>
          <p className="text-muted-foreground">
            We may update this Privacy Policy from time to time. The latest
            version will always be available at this URL and the “Effective
            Date” above will be updated.
          </p>

          <h2 className="text-xl font-semibold mt-8">12. Contact</h2>
          <p className="text-muted-foreground">
            CPN Trade Inc. / Lovi Creation —{' '}
            <a
              href="mailto:admin@lovicreation.shop"
              className="text-primary underline underline-offset-4 hover:opacity-80"
              data-testid="privacy-contact-email-footer"
            >
              admin@lovicreation.shop
            </a>
          </p>
        </section>

        <footer className="mt-16 pt-8 border-t border-border text-sm text-muted-foreground flex items-center justify-between">
          <span>© 2026 CPN Trade Inc. / Lovi Creation</span>
          <Link
            to="/eula"
            className="hover:text-foreground transition-colors"
            data-testid="privacy-footer-eula"
          >
            EULA →
          </Link>
        </footer>
      </main>
    </div>
  );
}
